import React, { useState, useCallback, useEffect } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormLabel,
  CFormInput,
  CFormTextarea,
  CFormSelect,
  CButton,
  CRow,
  CCol,
} from '@coreui/react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AxiosInstance from 'src/components/Axios';
import PermissionsNoPage, { Permissions } from 'src/components/Permissions';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import NotificationTypeMismatchModal from 'src/components/myComponents/NotificationTypeMismatchModal';
import { isTypeMismatch, detectNotificationType } from 'src/utils/notificationTypeCheck';

const urlRouter = '/massive-notifications';
const sendUrl = '/create-notfication-actions';

const groupOptions = [
  { value: 'all', label: 'Всім' },
  { value: 'all_active_30', label: 'Всі Активні користувачі (останні 30 днів)' },
  { value: 'all_not_logged_30', label: 'Не активні більше 30 днів' },
  // { value: 'all_not_logged', label: 'Не активні більше 30 днів' },
  { value: 'all_without_accounts', label: 'Всі без рахунків' },
  { value: 'all_with_accounts', label: 'Всі з рахунками' },
];

const UsersNotificationsCreate = ({ user_data }) => {
  // if (!Permissions(user_data, 'MassNotifications', 'VIEW_MASSNOTIFICATIONS')) {
  //   return <PermissionsNoPage />;
  // }
  const navigate = useNavigate();
  const [group, setGroup] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState('text');
  const [submitting, setSubmitting] = useState(false);
  const [counterGroupOptions, setCounterGroupOptions] = useState(groupOptions);
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const [detectedType, setDetectedType] = useState('text');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // тут можна додати логіку для отримання кількості користувачів в кожній групі
    // наприклад, якщо потрібно відобразити кількість користувачів в кожній групі
    const fetchGroupCounts = async () => {
      try {
        const response = await AxiosInstance.get('/users/stats_all');
        const counts = response.data;
        // console.log('Кількість користувачів в групах:', counts);
        if (!counts || typeof counts !== 'object') {
          console.error('Не вдалося отримати дані про кількість користувачів');
          return;
        }
        const updatedOptions = groupOptions.map((option) => ({
          ...option,
          label: `${option.label} к-ть. - (${counts[option.value] || 0})`,
        }));
        setCounterGroupOptions(updatedOptions);
      } catch (error) {
        console.error('Не вдалося отримати кількість користувачів:', error);
      }
    };
    fetchGroupCounts();
  }, []);

  // const groupOptions = counterGroupOptions.map((option) => (
  //   <option key={option.value} value={option.value}>
  //     {option.label}
  //   </option>
  // ));

  useEffect(() => {
    // очищення полів при переході на сторінку створення
    setGroup('');
    setTitle('');
    setBody('');
    setType('text');
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const sendNotification = async () => {
    setShowMismatchModal(false);
    setErrors({});
    setSubmitting(true);
    try {
      await AxiosInstance.post(sendUrl, {
        'sender': 'admin',
        'msg-type': 'notification-all',
        'msg-data': {
          'priority': 0,
          'ns_msg_id': `admin-${group}-${Date.now()}`,
          'type': type,
          'group': group,
          'title': title,
          'content': body,
        },
      });
      navigate(urlRouter, { state: { toast: { type: 'success', message: 'Сповіщення надіслано' } } });
    } catch (error) {
      const apiMessage = error?.response?.data?.message || 'Не вдалося створити сповіщення.';
      toast.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newErrors = { group: !group, title: !title.trim(), body: !body.trim() };
    if (newErrors.group || newErrors.title || newErrors.body) {
      setErrors(newErrors);
      const missing = [
        newErrors.group && 'Група',
        newErrors.title && 'Заголовок',
        newErrors.body && 'Текст',
      ].filter(Boolean);
      toast.warning(`Не заповнено: ${missing.join(', ')}`);
      return;
    }
    if (isTypeMismatch(type, body)) {
      setDetectedType(detectNotificationType(body));
      setShowMismatchModal(true);
      return;
    }
    await sendNotification();
  };

  return (
    <>
      <ToastContainer />
      <CCard className="mb-4">
        <CCardHeader>
          <strong>Створити сповіщення</strong>
        </CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleSubmit}>
            <FormSubmitOverlay submitting={submitting}>
              <CRow className="g-4">
                <CCol xs={6}>
                  <CFormLabel>Група отримувачів</CFormLabel>
                  <CFormSelect
                    value={group}
                    onChange={(event) => { setGroup(event.target.value); if (errors.group) setErrors(prev => ({ ...prev, group: false })); }}
                    invalid={!!errors.group}
                  >
                    <option value="">Оберіть групу</option>
                    {counterGroupOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol xs={6}>
                  <CFormLabel>Тип повідомлення</CFormLabel>
                  <CFormSelect
                    value={type}
                    onChange={(event) => setType(event.target.value)}
                  >
                    <option value="text">Текст</option>
                    <option value="html">HTML</option>
                  </CFormSelect>
                </CCol>
                <CCol xs={12}>
                  <CFormLabel>Заголовок</CFormLabel>
                  <CFormInput
                    type="text"
                    value={title}
                    onChange={(event) => { setTitle(event.target.value); if (errors.title) setErrors(prev => ({ ...prev, title: false })); }}
                    autoComplete="off"
                    invalid={!!errors.title}
                  />
                </CCol>
                <CCol xs={12}>
                  <CFormLabel>Текст повідомлення</CFormLabel>
                  <CFormTextarea
                    rows={8}
                    value={body}
                    onChange={(event) => { setBody(event.target.value); if (errors.body) setErrors(prev => ({ ...prev, body: false })); }}
                    invalid={!!errors.body}
                  />
                </CCol>
              </CRow>
              <div className="d-flex gap-3 mt-4">
                <CButton type="submit" color="success">
                  Створити
                </CButton>
                <CButton type="button" color="secondary" variant="outline" onClick={handleBack}>
                  Скасувати
                </CButton>
              </div>
            </FormSubmitOverlay>
          </CForm>
          <NotificationTypeMismatchModal
            visible={showMismatchModal}
            selectedType={type}
            detectedType={detectedType}
            onConfirm={sendNotification}
            onCancel={() => setShowMismatchModal(false)}
          />
        </CCardBody>
      </CCard>
    </>
  );
};

export default UsersNotificationsCreate;
